/**
 * Case studies and licensing examples. Add, remove or reorder entries freely.
 * Keep entries anonymised until SPC has written approval from each partner.
 */

export const licensingExamples = [
  {
    id: "photofirst-eris",
    area: "Dermatology",
    title: "Photofirst (Photocil) licensed to Eris Lifesciences",
    image: "/images/sunlight-hand.webp",
    challenge:
      "Photocil, a patented home phototherapy cream for vitiligo and psoriasis, needed regulatory approval in India and a dermatology partner with national reach.",
    did:
      "SPC supported the CDSCO approval and licensed exclusive India rights to Eris Lifesciences, which launched the product as Photofirst.",
    outcome:
      "Photofirst is on the market across India, supported by a patient companion app built by SPC.",
    to: "/products/photocil",
  },
  {
    id: "hair-latam",
    area: "Dermatology and hair science",
    title: "Patented hair-growth topical platform",
    image: "/images/dropper.webp",
    challenge:
      "An innovator held a patented topical hair-growth platform but had no route into Latin American markets, each with its own regulator and commercial landscape.",
    did:
      "SPC in-licensed the platform and sublicensed it across multiple LATAM territories to a major pharma company, with regulatory and commercial support.",
    outcome:
      "One partner, multiple territories, a single point of contact for the innovator.",
  },
  {
    id: "crc-india",
    area: "Diagnostics",
    title: "Colorectal cancer screening diagnostic",
    image: "/images/tubes.webp",
    challenge:
      "A colorectal cancer screening test needed a commercial model for India, where screening reach depends on lab networks and digital access as much as on the test itself.",
    did:
      "SPC commercialised the diagnostic through a joint venture, onboarding lab networks and digital health partners to reach patients and clinicians.",
    outcome:
      "A structured route to market combining laboratory capacity and digital channels.",
  },
  {
    id: "derm-app",
    area: "Dermatology",
    title: "Dermatology brand with companion app",
    image: "/images/serum.webp",
    challenge:
      "An established dermatology brand wanted deeper patient engagement and wider distribution in India.",
    did:
      "SPC extended the brand with a companion mobile app and secured distribution through a leading Indian pharma company.",
    outcome:
      "A brand that reaches patients through both prescribers and their phones.",
  },
];

/* ---------- /case-studies page ---------- */

export const caseFilters = [
  { id: "all", label: "All" },
  { id: "licensing-jv", label: "Licensing & JV" },
  { id: "market-access", label: "Market Access & Entry" },
  { id: "api-exports", label: "API Exports" },
  { id: "rd-cro", label: "R&D / CRO" },
];

// `regulatory: true` shows the in-house regulatory team note; optional `link` opens an external page.
export const caseStudyCards = [
  {
    id: "ofi-jv",
    category: "licensing-jv",
    title: "A joint venture to manufacture Italian dermocosmetics in India",
    markets: "Italy, India",
    summary: "SPC's joint venture with OFI, Italy, moved production of proven anti-ageing and whitening ranges to India.",
    challenge:
      "SPC had already introduced OFI's Yu Reverzo anti-ageing and whitening ranges to the Indian market with a leading pharmaceutical partner. Importing finished goods from Italy added freight and customs duty, which pushed up the cost of goods and limited how far the brands could scale.",
    did:
      "Set up a joint venture with OFI so the products, including plant stem cell based anti-ageing and whitening formulations, can be manufactured in India. SPC manages the technology transfer, local manufacturing and the relationship with Indian marketing partners.",
    result:
      "A significantly lower cost of goods for Indian partners, a more competitive shelf price, and a platform to offer the full OFI portfolio to pharmaceutical and dermatology companies in India.",
  },
  {
    id: "sifi-zydus",
    category: "licensing-jv",
    title: "Bringing Italian ophthalmic innovation to India with a top Indian pharma company",
    markets: "Italy, India",
    summary: "SPC partnered SIFI, Italy, with Zydus to commercialise advanced ophthalmic products, including intraocular lenses, in India.",
    challenge:
      "SIFI, an Italian ophthalmology specialist, needed a strong Indian commercial partner for its premium intraocular lens portfolio, and a local team to keep the partnership running smoothly after launch.",
    did:
      "Identified and brought in Zydus as the Indian partner, structured the commercial relationship, and continues to manage it: joint business reviews, portfolio planning and solving commercial issues between the two companies as they come up.",
    result:
      "SIFI's lens portfolio is available to Indian surgeons through one of India's largest pharmaceutical companies, with SPC as the ongoing bridge between the Italian and Indian teams.",
  },
  {
    id: "photocil-eris",
    category: "licensing-jv",
    product: "/products/photocil",
    title: "Exclusive India rights for Photocil with Eris Oaknet",
    markets: "India",
    summary: "SPC licensed Photocil to Eris Oaknet with exclusive rights for India, launched as Photofirst.",
    challenge:
      "Photocil, a patented home phototherapy cream for vitiligo and psoriasis, needed regulatory approval in India and a dermatology partner with national reach.",
    did:
      "Supported the CDSCO approval, licensed exclusive India rights to Eris Oaknet, supported a launch to more than 100 dermatologists in Mumbai, and later built and licensed the Photofirst Companion App, now in 11 Indian languages.",
    result:
      "Photofirst is on the market across India with a patient app that supports adherence and gives the marketing partner real-world usage data.",
    link: {
      label: "Read the launch news",
      href: "https://www.accessnewswire.com/newsroom/en/healthcare-and-pharmaceutical/eris-oaknet-healthcare-and-cosmofix-technovation-announce-market-launc-726352",
    },
  },
  {
    id: "hair-enzyme-booster",
    category: "licensing-jv",
    product: "/products/minoxidil-booster",
    title: "Licensing the Hair Enzyme Booster to India's leading pharma companies",
    markets: "India, Asia, Middle East, Latin America",
    summary: "SPC licensed the Hair Enzyme Booster to Glenmark, Dr. Reddy's, Cipla, Cosmofix, Eris and Dabur.",
    challenge:
      "Around 40% of minoxidil users do not respond to treatment. The Hair Enzyme Booster addresses the enzyme behind this, but it needed strong commercial partners to reach patients at scale.",
    did:
      "Structured multiple non-exclusive licensing agreements so each partner could launch the booster under its own brand, supplied finished product from India, and worked with partners on registrations in Australia, the Philippines, Russia and Latin America.",
    result:
      "The booster is sold under several leading brands, including Dr. Reddy's Mintop Booster, giving the technology far wider reach than any single licensee could.",
    link: { label: "See it in market", href: "https://jupiter.1mg.com/otc/mintop-booster-solution-otc971902" },
  },
  {
    id: "tyrosinase-inhibitor",
    category: "licensing-jv",
    title: "A novel skin-brightening ingredient for India (coming soon)",
    markets: "China, India",
    summary: "SPC is finalising India distribution of a patented, next-generation tyrosinase inhibitor for hyperpigmentation and melasma.",
    challenge:
      "Indian dermatology companies want hydroquinone-free alternatives with strong clinical data. A new patented active from an innovator in China needed a local partner to register it and supply manufacturers.",
    did:
      "Negotiated an India distribution and supply arrangement with the innovator, committed to register the ingredient in India, lined up a large Indian contract manufacturer as the first customer, and is negotiating the supply agreement terms.",
    result: "The first finished products are planned for launch in India in early 2027.",
  },
  {
    id: "postbiotic-muscle",
    category: "licensing-jv",
    title: "A novel food ingredient for muscle health (coming soon)",
    markets: "South Korea, India",
    summary: "SPC is bringing a clinically studied postbiotic for muscle preservation from Korea to India.",
    challenge:
      "Patients on GLP-1 weight-loss medicines can lose muscle along with fat. A Korean innovator had a postbiotic ingredient with clinical data on muscle health but no route into India.",
    did:
      "Proposed licensing terms for India, ran a gap analysis of the Indian regulatory requirements, worked out import classification and duty, and is presenting the ingredient to leading Indian pharmaceutical companies as a GLP-1 companion and sarcopenia product.",
    result: "Partner evaluations are under way ahead of an Indian launch.",
  },
  {
    id: "zambon-india",
    category: "market-access",
    title: "Supporting a European pharmaceutical group's presence in India",
    markets: "Italy, India",
    summary: "SPC worked with Zambon, Italy, on its India operations.",
    challenge:
      "An international pharmaceutical company entering India needs a trusted local partner who understands company set-up, governance, regulatory requirements and the commercial landscape.",
    did:
      "Supported Zambon's India business at both the board and operational level, covering local entity governance, compliance and commercial operations, and continues to assist on India-specific matters.",
    result: "A long-standing relationship that gave a European group local leadership and know-how in India.",
  },
  {
    id: "kedrion-plasma",
    category: "market-access",
    title: "Market access for plasma-derived therapies",
    markets: "Italy, India",
    summary: "SPC supported Kedrion, the Italian plasma-derived therapies company, with India market access and entry.",
    challenge:
      "Plasma-derived therapies are highly regulated biologics. Entering India requires local regulatory knowledge, the right commercial partner and careful structuring.",
    did:
      "Supported Kedrion's India market entry and access strategy, and later introduced Kedrion to Zydus, putting a confidentiality agreement in place and facilitating discussions on plasma products for India.",
    result: "A structured route for a specialist biologics company into one of the world's largest pharmaceutical markets.",
  },
  {
    id: "semaglutide-latam-africa",
    category: "market-access",
    title: "Registering generic semaglutide (GLP-1) in Latin America and Africa",
    markets: "Central America, Morocco, wider Africa",
    summary: "SPC is taking an Indian manufacturer's semaglutide pre-filled pens into new markets as GLP-1 patents expire.",
    challenge:
      "Following the 2026 semaglutide patent transitions, distributors in Latin America and Africa are looking for reliable Indian manufacturers of finished pens. Each market has its own dossier rules, stability requirements (for example Zone IVb) and cold chain needs.",
    did:
      "Connected the manufacturer with distribution partners in Morocco and El Salvador, ran a dossier gap analysis against Moroccan requirements, negotiated the licensing and supply term sheet, and is coordinating the CTD dossier, WHO-format CPP, stability data, strengths, pricing and Spanish labelling for Central America.",
    result:
      "Registration work is underway in Morocco, and a launch order is under evaluation for El Salvador with expansion planned across Central America.",
  },
  {
    id: "ceftazidime-avibactam-korea",
    category: "api-exports",
    regulatory: true,
    title: "Sterile antibiotic premix for two Korean drug makers",
    markets: "South Korea",
    summary: "Patent clearance and a sterile 4:1 ceftazidime + avibactam premix for two major Korean companies.",
    challenge:
      "Two of Korea's larger pharmaceutical companies needed a sterile, injection-grade ceftazidime and avibactam blend in a fixed 4:1 ratio for their own product development. Avibactam is covered by several process and crystal-form patents.",
    did:
      "Found a capable Indian manufacturer and brought its existing direct talks into one coordinated channel. SPC's regulatory team took four international patent families plus a pending Korean application through review and obtained written non-infringement confirmation, then put together the COA, stability data, blend specification and R&D and commercial quotations.",
    result: "One clear supply channel and a settled patent position before the buyers began development.",
  },
  {
    id: "ibandronate-korea",
    category: "api-exports",
    regulatory: true,
    title: "Nearly half of a national API market for an osteoporosis drug",
    markets: "South Korea",
    summary: "Ibandronate sodium supply, channel protection and full supplier qualification.",
    challenge:
      "Korean demand for ibandronate is small (about 75 kg a year) and spread across a few producers and around 50 contract manufacturers, many of which were contacting the API maker directly.",
    did:
      "Worked with its Korean partner to sort more than 60 companies into real buyers and those only checking prices. Agreed a rule with the manufacturer that direct inquiries go back to the authorised channel. SPC's regulatory team built a 30+ document supplier qualification dossier covering site, quality systems, impurity and nitrosamine assessments, certificates and validation.",
    result: "SPC's channel supplied 36.5 kg in 2025, close to half of Korea's total demand.",
  },
  {
    id: "propofol-korea",
    category: "api-exports",
    regulatory: true,
    title: "Entering Korea with a controlled anaesthetic API",
    markets: "South Korea",
    summary: "Propofol market entry, with price matching for a major user.",
    challenge:
      "Propofol is a controlled psychotropic substance in Korea. A Korean injectables maker using about 500 kg a year needed a new source because rising costs were threatening its production.",
    did:
      "Built a market entry plan with the Indian manufacturer, mapped seven target buyers, appointed a Korean partner for registration and customer contact, and negotiated pricing to meet the customer's target. SPC's in-house regulatory team handled the documentation.",
    result: "A landed price below the customer's target and a structured entry route for a manufacturer new to Korea.",
  },
  {
    id: "cetirizine-kdmf",
    category: "api-exports",
    regulatory: true,
    title: "Taking a first-time exporter through Korean DMF registration",
    markets: "South Korea",
    summary: "From approved sample to KDMF dossier review for cetirizine dihydrochloride.",
    challenge: "A Korean buyer approved an Indian manufacturer's sample, but the manufacturer had never filed a DMF in a regulated market.",
    did:
      "SPC's regulatory team assembled the GMP certificate, DMF, stability data and site master file, and answered every buyer question on API-specific GMP, WHO GMP renewal, CoPP, inspection history and bioequivalence history.",
    result: "A manufacturer with no regulated-market history moved into active KDMF dossier review.",
  },
  {
    id: "generics-eu-brazil",
    category: "api-exports",
    regulatory: true,
    title: "Sourcing generic APIs for Europe and Latin America",
    markets: "Italy, Brazil",
    summary: "CEP grade generics for a European contract manufacturer and an API portfolio for a major Brazilian drug maker.",
    challenge:
      "A long-standing Italian customer needed CEP and EU-GMP grade cardiovascular APIs for 2027 production, but several manufacturers declined, citing existing agents. Separately, a large Brazilian company asked what APIs SPC could source from India.",
    did:
      "Verified the manufacturers' claims directly with the end customer, approached an alternative producer, and prepared matched offers. For Brazil, prepared a non-confidential API portfolio that introduces manufacturers product by product once there is firm interest. SPC's in-house regulatory team handled the documentation.",
    result: "A second supply option for the European customer and a confidential, structured sourcing route for the Brazilian buyer.",
  },
  // R&D / CRO programmes (confidential: approved wording only). `markets` holds the location.
  {
    id: "hair-science-dossier",
    category: "rd-cro",
    title: "Building the clinical and safety dossier for a hair science product",
    markets: "Mumbai, India",
    summary: "SPC ran the clinical, safety and stability programme behind a licensed hair science product, now supporting registrations in several countries.",
    challenge:
      "The innovator needed product-level clinical evidence, human safety data and long-term stability data that regulators in Latin America and other markets would accept, including in countries that do not allow animal-based safety testing.",
    did:
      "Organised a GCP-compliant, open-label clinical study in India with leading dermatologists, measuring the product's effect on its biological target in the hair follicle. Commissioned a human skin irritation patch test at an accredited CRO in Mumbai, and ran three production batches on ICH Zone IVb stability. SPC's regulatory team assembled the results into a single dossier for licensing partners.",
    result:
      "The product was certified non-irritant in human testing, completed 24 months of Zone IVb stability with all parameters within specification, and the data package is now being used for registrations by partners abroad.",
  },
  {
    id: "long-acting-injectable",
    category: "rd-cro",
    title: "Developing a long-acting injectable with a leading Indian CDMO",
    markets: "India, for an overseas biotech innovator",
    summary: "SPC structured and manages a development programme for a novel long-acting depot injection at one of India's leading CDMOs.",
    challenge:
      "An overseas biotech company had a promising long-acting injectable concept but needed a capable development partner, cost control and a clear, staged plan before committing to a full programme.",
    did:
      "Identified the CDMO, put the confidentiality agreement, MOU and commercial agreements in place between all parties, ran the technical alignment calls on the formulation approach, and set up a phased plan that starts with a capped feasibility stage. SPC acts as project manager and the single point of contact between the innovator and the CDMO.",
    result: "Agreements were signed by all parties in October 2026 and the feasibility stage is under way.",
  },
  {
    id: "oral-film",
    category: "rd-cro",
    title: "Developing a novel oral film formulation",
    markets: "India",
    summary: "SPC is managing the development of a novel oral thin-film product for an international partner.",
    challenge:
      "Oral films dissolve in the mouth without water, which suits patients who struggle with tablets, but they need specialist formulation, manufacturing and stability expertise.",
    did:
      "Matched the project with a specialist Indian development partner, coordinated formulation development and testing, and is aligning the stability and regulatory work with the partner's target markets.",
    result: "Development is in progress.",
  },
  {
    id: "recombinant-ncl",
    category: "rd-cro",
    title: "Scaling up a recombinant biologic with CSIR-NCL",
    markets: "Pune, India, for a UK biotech innovator",
    summary:
      "SPC and its development partners brought a UK yeast-based recombinant expression platform to CSIR-National Chemical Laboratory (NCL), one of India's leading national research institutes, for fermentation scale-up of a recombinant biologic.",
    challenge:
      "The innovator had a high-yield yeast expression platform that could make recombinant biologics at a lower cost than conventional routes. It needed an Indian partner with fermentation and process-engineering expertise to prove the process at larger scale before moving to commercial manufacturing.",
    did:
      "Working with its Indian development partners, SPC helped connect the innovator with NCL's scientists, supported the technical and fermentation planning, and handled commercial steps such as confidentiality agreements and contracting with a government research institute. SPC is also presenting the platform to Indian pharmaceutical companies as a technology licensing opportunity.",
    result: "The production strain was successfully fermented at 10-litre scale in India, and options for commercial-scale manufacturing are now being evaluated.",
  },
];
